// Componente recursivo para renderizar a árvore de permissões com checkboxes.
//
// Modelo de dados (FLOW PermissionChecker.cs:44): cada string de permissão é
// gravada individualmente. Não há herança em runtime — pai não concede filhas
// automaticamente.
//
// UX de seleção (puramente visual, não afeta a semântica de gravação):
//   - Folha: clica no checkbox → toggle daquela única permissão.
//   - Pai: clica no checkbox → toggle EM CASCATA (marca/desmarca o nó pai +
//     todos os descendentes). Atalho útil para conceder uma área inteira.
//   - Pai com seleção parcial → checkbox indeterminado (visual hint).
//
// Cada string ainda é enviada individualmente para o backend no save —
// o cascade aqui é só conveniência de UI.

import React, { useRef, useEffect } from "react";

function collectSubtreeNames(node, acc = []) {
  if (node.name) acc.push(node.name);
  if (node.children) {
    for (const child of Object.values(node.children)) {
      collectSubtreeNames(child, acc);
    }
  }
  return acc;
}

function countDescendants(node) {
  if (!node.children) return 0;
  const childArr = Object.values(node.children);
  let total = childArr.length;
  for (const c of childArr) total += countDescendants(c);
  return total;
}

function countSelectedDescendants(node, selectedSet) {
  if (!node.children) return 0;
  let count = 0;
  for (const c of Object.values(node.children)) {
    if (selectedSet.has(c.name)) count += 1;
    count += countSelectedDescendants(c, selectedSet);
  }
  return count;
}

function CheckboxInput({ checked, indeterminate, onChange, ariaLabel }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      aria-label={ariaLabel}
      style={{ cursor: "pointer" }}
    />
  );
}

function TreeNode({ node, depth, selectedSet, onToggle, onToggleSubtree }) {
  const isLeaf = !node.children || Object.keys(node.children).length === 0;
  const childArr = node.children ? Object.values(node.children) : [];

  const totalDescendants = isLeaf ? 0 : countDescendants(node);
  const selectedDescendants = isLeaf
    ? 0
    : countSelectedDescendants(node, selectedSet);

  const selfChecked = selectedSet.has(node.name);

  // Para folhas, o estado do checkbox é só o self.
  // Para pais, considera o self + descendentes:
  //   - todos marcados → checked
  //   - alguns marcados → indeterminate
  //   - nenhum marcado → unchecked
  let checked = selfChecked;
  let indeterminate = false;
  if (!isLeaf) {
    const selfPlusDescTotal = totalDescendants + 1;
    const selfPlusDescSelected = selectedDescendants + (selfChecked ? 1 : 0);
    checked = selfPlusDescSelected === selfPlusDescTotal;
    indeterminate = selfPlusDescSelected > 0 && !checked;
  }

  const handleChange = () => {
    if (isLeaf) {
      onToggle(node.name);
    } else {
      // Pai: cascade. Se já está totalmente marcado → desmarca tudo;
      // caso contrário (parcial ou vazio) → marca tudo.
      const subtreeNames = collectSubtreeNames(node);
      onToggleSubtree(subtreeNames, !checked);
    }
  };

  return (
    <>
      <div
        style={{
          paddingLeft: depth * 20,
          paddingTop: 4,
          paddingBottom: 4,
          borderBottom: depth === 0 ? "1px solid #2b3553" : "none",
          marginTop: depth === 0 ? 8 : 0,
        }}
      >
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
            color: "#f5f7fb",
            fontSize: 13,
            margin: 0,
          }}
        >
          <CheckboxInput
            checked={checked}
            indeterminate={indeterminate}
            onChange={handleChange}
            ariaLabel={node.displayName || node.name}
          />
          <span style={{ fontWeight: depth === 0 ? 600 : 400 }}>
            {node.displayName || node.name}
          </span>
          <span style={{ color: "#8fa3c8", fontSize: 11, marginLeft: 4 }}>
            ({node.name})
          </span>
          {!isLeaf && (
            <span
              style={{
                marginLeft: "auto",
                fontSize: 11,
                color: "#8fa3c8",
                whiteSpace: "nowrap",
              }}
            >
              {selectedDescendants}/{totalDescendants} marcadas
            </span>
          )}
        </label>
      </div>
      {childArr.map((child) => (
        <TreeNode
          key={child.name}
          node={child}
          depth={depth + 1}
          selectedSet={selectedSet}
          onToggle={onToggle}
          onToggleSubtree={onToggleSubtree}
        />
      ))}
    </>
  );
}

export default function PermissionTreeView({
  tree,
  selectedNames,
  onToggle,
  onToggleSubtree,
}) {
  if (!tree) return null;
  const selectedSet =
    selectedNames instanceof Set ? selectedNames : new Set(selectedNames || []);
  return (
    <div>
      <TreeNode
        node={tree}
        depth={0}
        selectedSet={selectedSet}
        onToggle={onToggle}
        onToggleSubtree={onToggleSubtree}
      />
    </div>
  );
}

// Exporta para o modal usar nos botões "Marcar todas / Desmarcar todas".
export { collectSubtreeNames };
